import { useEffect, useCallback, useRef } from 'react';
import { createParser } from 'eventsource-parser';
import { getTimeZone } from '../lib/axios';

const useSSE = (url, onData, onError, options = {}) => {
  const { autoReconnect = true, reconnectInterval = 5000 } = options;

  // Guards & controllers to avoid multiple connections
  const startedRef = useRef(false);
  const controllerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const startTimeoutRef = useRef(null);

  const connectSSE = useCallback(async () => {
    // If already started, do nothing
    if (startedRef.current) {
      // console.debug('[useSSE] already started, skipping connect');
      return;
    }

    controllerRef.current = new AbortController();

    try {
      const accessToken = sessionStorage.getItem('accessToken');
      const baseUrl = process.env.REACT_APP_BE_URL || '';
      const domainUrl = window.location.hostname;
      
      const finalUrl = new URL(
        url.startsWith('/') ? url : '/' + url,
        baseUrl
      );
      
      console.debug('[useSSE] connecting to', finalUrl);
      
      const response = await fetch(finalUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'text/event-stream',
          timezone: getTimeZone(),
          domain:
            domainUrl === 'localhost' ||
            domainUrl === 'classified-ads-c4c4a.web.app'
              ? 'dev.oxiwear.fitness'
              : domainUrl,
        },
        signal: controllerRef.current.signal,
      });

      if (!response.ok) {
        onError?.(`Failed to connect: ${response.status}`);
        // schedule reconnect if enabled
        if (autoReconnect) {
          reconnectTimeoutRef.current = setTimeout(
            () => connectSSE(),
            reconnectInterval
          );
        }
        return;
      }

      // Mark as started
      startedRef.current = true;

      const reader = response.body?.getReader();
      if (!reader) {
        onError?.('No readable stream in response');
        return;
      }

      const parser = createParser({
        onEvent: (event) => {
          if (!event.data) return;
          try {
            const parsedData = JSON.parse(event.data);
            onData?.(parsedData);
          } catch (error) {
            onError?.(`Error parsing SSE data: ${error.message}`);
          }
        },
        onError: (error) => {
          onError?.(`Parser error: ${error?.message || error}`);
        },
      });

      // Read loop
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const decoded = new TextDecoder().decode(value);
        parser.feed(decoded);
      }

      // stream ended normally
      startedRef.current = false;
      if (autoReconnect) {
        reconnectTimeoutRef.current = setTimeout(() => connectSSE(), reconnectInterval);
      }
    } catch (error) {
      // If aborted by cleanup, ignore
      if (error && error.name === 'AbortError') {
        console.debug('[useSSE] fetch aborted');
        return;
      }

      startedRef.current = false;
      onError?.(`Connection error: ${error?.message || error}`);

      if (autoReconnect) {
        reconnectTimeoutRef.current = setTimeout(() => connectSSE(), reconnectInterval);
      }
    }
  }, [url, onData, onError, autoReconnect, reconnectInterval]);

  useEffect(() => {
    // If URL (members) changed, abort previous connection and allow reconnect
    if (controllerRef.current) {
      try {
        controllerRef.current.abort();
      } catch (e) {
        console.error('[useSSE] abort previous controller failed', e);
      }
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }

    // allow new connection
    startedRef.current = false;

    // wait 5 seconds then start connection
    startTimeoutRef.current = setTimeout(() => {
      connectSSE();
    }, 2000);

    return () => {
      // cleanup
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }
      try {
        controllerRef.current?.abort();
      } catch (e) {
        console.error(e);
      }
      startedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
    // reconnect when connectSSE or url changes
  }, [connectSSE, url]);

};

export default useSSE;
